import { userErrorKey } from '@/utils/userErrors.mjs';
import { defineStore } from 'pinia';
import { imClient } from '@/services/imClient';
import { appConfig } from '@/config';
import { toPortableObjectPath } from '@/utils/assetUrl';

const STORAGE_KEY = 'qmt-web-im-session';

function normalizeStoredPortrait(portrait) {
  return toPortableObjectPath(portrait || '');
}

function nextProfilePortrait(profilePortrait, currentPortrait) {
  return normalizeStoredPortrait(profilePortrait) || normalizeStoredPortrait(currentPortrait);
}

function errorReferenceCode(error) {
  const data = error?.response?.data || error?.data || {};
  const code = Number(data.errCode ?? error?.errCode);
  if (Number.isInteger(code) && code > 0) return String(code);
  const status = Number(error?.response?.status || error?.status);
  return status >= 400 ? `HTTP ${status}` : '';
}

export const useSessionStore = defineStore('session', {
  state: () => ({
    isAuthenticated: false,
    connecting: false,
    connected: false,
    connectionStatusText: '未连接',
    userId: '',
    token: '',
    chatToken: '',
    displayName: '',
    portrait: '',
    error: '',
    errorKey: '',
    errorCode: '',
    sdkVersion: '',
    eventsBound: false
  }),
  actions: {
    restore() {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return;
      }
      try {
        const session = JSON.parse(raw);
        if (session.appServer !== appConfig.appServer) {
          localStorage.removeItem(STORAGE_KEY);
          return;
        }
        if (Number(session.platform) !== Number(appConfig.platform)) {
          localStorage.removeItem(STORAGE_KEY);
          this.errorKey = 'auth.sessionExpired';
          return;
        }
        if (session.userId && session.token) {
          if (!session.clientId) {
            localStorage.removeItem(STORAGE_KEY);
            return;
          }
          this.userId = session.userId;
          this.token = session.token;
          this.chatToken = session.chatToken || '';
          this.displayName = session.displayName || session.userId;
          this.portrait = normalizeStoredPortrait(session.portrait);
          this.isAuthenticated = true;
          imClient.restoreClientId(session.clientId);
          if (session.localOnly) {
            this.connectionStatusText = '本地调试登录';
          }
          this.connect().catch(() => {});
          this.refreshProfile();
        }
      } catch (error) {
        localStorage.removeItem(STORAGE_KEY);
      }
    },
    async loginWithPassword(mobile, password, areaCode = null) {
      this.error = '';
      this.errorKey = '';
      this.errorCode = '';
      this.connecting = true;
      try {
        const result = await imClient.loginWithPassword(mobile, password, areaCode);
        this.applyLoginResult(result);
        await this.connect();
        await this.refreshProfile();
      } catch (error) {
        this.errorCode = errorReferenceCode(error);
        const sdkConnectionFailed = this.isAuthenticated && !this.connected;
        await this.rollbackUnconnectedLogin();
        if (sdkConnectionFailed) {
          this.error = '';
          this.errorKey = 'auth.connectionFailed';
        } else if (!this.errorKey) {
          this.error = '';
          this.errorKey = 'error.' + userErrorKey(error, 'login');
        }
      } finally {
        this.connecting = false;
      }
    },
    async loginWithCode(mobile, code) {
      this.error = '';
      this.errorKey = '';
      this.errorCode = '';
      this.connecting = true;
      try {
        const result = await imClient.loginWithCode(mobile, code);
        this.applyLoginResult(result);
        await this.connect();
        await this.refreshProfile();
      } catch (error) {
        this.errorCode = errorReferenceCode(error);
        const sdkConnectionFailed = this.isAuthenticated && !this.connected;
        await this.rollbackUnconnectedLogin();
        if (sdkConnectionFailed) {
          this.error = '';
          this.errorKey = 'auth.connectionFailed';
        } else if (!this.errorKey) {
          this.error = '';
          this.errorKey = 'error.' + userErrorKey(error, 'login');
        }
      } finally {
        this.connecting = false;
      }
    },
    async registerWithPhone(mobile, code) {
      this.error = '';
      this.errorKey = '';
      this.connecting = true;
      try {
        return await imClient.registerWithPhone(mobile, code);
      } catch (error) {
        this.error = error.message || '注册失败';
        return null;
      } finally {
        this.connecting = false;
      }
    },
    applyLoginResult(result) {
      this.userId = result.userId || result.uid || result.id || '';
      this.token = result.token || result.imToken || '';
      this.chatToken = result.chatToken || '';
      this.displayName = result.displayName || result.userName || result.name || this.userId;
      this.portrait = normalizeStoredPortrait(result.portrait);
      this.isAuthenticated = Boolean(this.userId && this.token);
      if (result.localOnly) {
        this.connectionStatusText = '本地调试登录';
      }
      this.persistSession(Boolean(result.localOnly));
    },
    async rollbackUnconnectedLogin() {
      if (!this.isAuthenticated || this.connected) {
        return;
      }
      await imClient.disconnect().catch(() => {});
      localStorage.removeItem(STORAGE_KEY);
      const eventsBound = this.eventsBound;
      this.$reset();
      this.eventsBound = eventsBound;
    },
    persistSession(localOnly = false) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        userId: this.userId,
        token: this.token,
        chatToken: this.chatToken,
        displayName: this.displayName,
        portrait: this.portrait,
        clientId: imClient.getClientId(),
        platform: appConfig.platform,
        localOnly,
        appServer: appConfig.appServer
      }));
    },
    applyProfile(profile) {
      if (!profile) {
        return;
      }
      this.userId = profile.userId || profile.uid || this.userId;
      this.displayName = profile.displayName || profile.name || this.displayName || this.userId;
      this.portrait = nextProfilePortrait(profile.portrait, this.portrait);
      this.persistSession();
    },
    async refreshProfile() {
      if (!this.userId) {
        return null;
      }
      try {
        const profile = await imClient.getProfile(this.userId);
        this.applyProfile(profile);
        return profile;
      } catch (error) {
        return null;
      }
    },
    async saveProfile({ displayName, portraitFile }) {
      if (!this.userId) {
        throw new Error('请先登录');
      }
      const profile = await imClient.updateProfile(this.userId, {
        displayName,
        portraitFile
      });
      this.applyProfile(profile);
      return profile;
    },
    async changePassword({ oldPassword, newPassword }) {
      if (!this.userId) {
        throw new Error('请先登录');
      }
      await imClient.changePassword({ oldPassword, newPassword });
      return true;
    },
    async resetPasswordWithCode({ mobile, resetCode, newPassword }) {
      await imClient.resetPasswordWithCode({ mobile, resetCode, newPassword });
      return true;
    },
    async connect() {
      if (!this.userId || !this.token) {
        return;
      }
      await imClient.ensureSdk();
      this.sdkVersion = imClient.getSdkVersion();
      this.bindImEvents();
      await imClient.connect(this.userId, this.token, {
        chatToken: this.chatToken,
        displayName: this.displayName,
        portrait: this.portrait
      });
    },
    bindImEvents() {
      if (this.eventsBound) {
        return;
      }
      this.eventsBound = true;
      imClient.on('connectionStatusChanged', status => {
        this.connected = status.connected;
        this.connectionStatusText = status.statusText || (status.connected ? '已连接' : '未连接');
      });
      imClient.on('authenticationInvalid', event => {
        localStorage.removeItem(STORAGE_KEY);
        const eventsBound = this.eventsBound;
        this.$reset();
        this.eventsBound = eventsBound;
        this.errorKey = event?.errorKey || 'auth.sessionExpired';
        imClient.disconnect().catch(() => {});
      });
    },
    logout() {
      imClient.disconnect();
      localStorage.removeItem(STORAGE_KEY);
      this.$reset();
    }
  }
});
